/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { Checkbox, FlexContainer, Text, theme } from "@nordcloud/gnui";
import {
  DiscoveryRule,
  DiscoveryRuleMatch,
  KeyValue,
} from "~/generated/graphql";
import { splitType } from "~/hooks/useDiscoveryRules/utils";
import { isNotEmpty } from "~/tools";
import { useFormContext } from "../context";
import { DiscoveredRecords, Filters } from "../DiscoveredRecords";
import {
  DiscoveryRuleMatchInput,
  DiscoveryRuleMatchKeys,
  RuleType,
} from "../types";
import { mapLabels } from "../utils";
import { AddRuleForm } from "./AddRuleForm";
import { DiscoveryRuleMatchList } from "./DiscoveryRuleMatchList/DiscoveryRuleMatchList";
import { FormControlLabel } from "./FormControlLabel";

type Props = {
  rule: DiscoveryRule;
  title: string;
  editable: boolean;
  removable: boolean;
};

export function DiscoveryRuleItem({ rule, title, editable, removable }: Props) {
  const [showInput, setShowInput] = useState(false);
  const { updateRule, deleteRule, deleteEmptyRule } = useFormContext();

  const { match = {} } = rule;

  const excludeSelectOptions = filterSelectors(match);

  const handleSubmit = (
    id: string,
    newMatch: DiscoveryRuleMatchInput,
    ibmResourceGroup?: boolean
  ) => {
    updateRule(id, {
      match: newMatch,
      exclusive: rule.exclusive,
      ibmResourceGroup,
    });
    setShowInput(false);
  };

  const handleDelete = (
    ruleType: string,
    ruleValue: KeyValue | string | undefined
  ) => deleteRule(rule.id, ruleType as DiscoveryRuleMatchKeys, ruleValue);

  const hasItems = isNotEmpty(Object.keys(match));

  const showForm = !hasItems || showInput;

  const showDiscoveredRecords = editable && hasItems && !showForm;

  useEffect(() => {
    // ref usage is preferred but problematic in this case
    const element = document.getElementById(rule.id);

    if (element instanceof HTMLElement) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [rule]);

  const handleClose = () => {
    if (!hasItems) {
      deleteEmptyRule(rule.id);
    }
  };

  return (
    <div
      css={{
        marginBottom: theme.spacing.spacing04,
        ".react-select__menu": {
          zIndex: theme.zindex.masked,
          // Due to a bug with footer/select menu overflowing, `position: absolute` is overwritten
          position: "static",
        },
      }}
    >
      <FlexContainer
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={theme.spacing.spacing04}
      >
        <Text weight="medium" mb="0">
          {title}
        </Text>
        {editable && (
          <FormControlLabel
            label="Excluded"
            labelPlacement="start"
            htmlFor={title}
            control={
              <Checkbox
                withoutLabel
                id={title}
                checked={rule.exclusive}
                onChange={() =>
                  updateRule(rule.id, {
                    exclusive: !rule.exclusive,
                  })
                }
              />
            }
            css={{
              fontSize: theme.fontSizes.sm,
            }}
          />
        )}
      </FlexContainer>
      <DiscoveryRuleMatchList
        match={match}
        editable={editable}
        isIbmResourceGroup={rule.ibmResourceGroup ?? false}
        onDelete={(ruleType, ruleValue) => handleDelete(ruleType, ruleValue)}
        onAdd={(item) => handleSubmit(rule.id, item)}
      />
      {showForm && (
        <AddRuleForm
          handleDelete={handleDelete}
          id={rule.id}
          excludeOptions={excludeSelectOptions}
          onSubmit={handleSubmit}
          onClose={removable ? handleClose : undefined}
        />
      )}
      {showDiscoveredRecords && (
        <DiscoveredRecords
          filters={mapFilters(match)}
          onClick={() => setShowInput(true)}
        />
      )}
    </div>
  );
}

function mapFilters(match: DiscoveryRuleMatch): Filters {
  return {
    ...(match.cloudAccount && { cloudAccount: match.cloudAccount }),
    ...(match.projectId && { projectId: match.projectId }),
    ...(match.resourceGroup && { resourceGroup: match.resourceGroup }),
    ...(match.type && splitType(match.type)),
    ...(match.tags && {
      tags: match.tags.map((tag) => ({ key: tag.key, values: [tag.value] })),
    }),
    ...(match.securityGroupIds && {
      securityGroupIds: match.securityGroupIds,
    }),
  };
}

function filterSelectors(match: DiscoveryRuleMatch) {
  const selectors = Object.keys(match) as RuleType[];

  const filtered = selectors.includes(RuleType.RESOURCE_GROUP)
    ? [...selectors, RuleType.IBM_RESOURCE_GROUP]
    : selectors;

  return filtered.map<string>(mapLabels);
}
